/**
 * Single entry point loaded by Main_Layout.cshtml on every page.
 *
 * - A Razor view for a converted template writes <script id="page-data"> with
 *   { component, props } and renders <div id="react-root">.
 * - This file looks up the component by name, loads only that page's chunk,
 *   and renders it. Unconverted (AngularJS) pages have no page-data, so
 *   nothing happens and React is never downloaded.
 *
 * Keep react / react-dom out of top-level imports here: they are imported
 * dynamically inside boot() so non-React pages only download this tiny file.
 */
import Footer from "./layout/footer";
import type { PageData, PageLoader } from "./shared/types";
import { getValidToken } from "./shared/auth/token.service";
import { config } from "./shared/config";

type Access = "public" | "protected" | "guest";
interface PageEntry {
    load: PageLoader;
    access: Access;
}

// Website pages: never checked.
const website = (load: PageLoader): PageEntry => ({ load, access: "public" });

// Portal pages: login required unless marked "guest".
const portal = (load: PageLoader, access: Access = "protected"): PageEntry => ({ load, access });

// Bot pages: login required unless marked "guest".
const bot = (load: PageLoader): PageEntry => ({ load, access: "public" });

// One line per converted Umbraco template.
const pages: Record<string, PageEntry> = {
    // --- Website (public) ---
    ClaimLogin: website(() => import("./website/pages/flowClaim/login")),
    ClaimDashboard: website(() => import("./website/pages/flowClaim/dashboard")),
    ClaimFile: website(() => import("./website/pages/flowClaim/file")),
    ClaimSubmitted: website(() => import("./website/pages/flowClaim/submitted")),
    ClaimEvidence: website(() => import("./website/pages/flowClaim/evidence")),
    // --- Portal ---
    PortalLogin: portal(() => import("./portal/pages/login"), "guest"),
    PortalDashboard: portal(() => import("./portal/pages/dashboard")),

    // --- Bot (public) ---
    Bot: bot(() => import("./bot")),
};

async function boot() {
    const dataEl = document.getElementById("page-data");
    const rootEl = document.getElementById("root");
    if (!dataEl || !rootEl) return; // not a React page

    let data: PageData;
    try {
        data = JSON.parse(dataEl.textContent ?? "");
    } catch (err) {
        console.error("Invalid JSON in #page-data", err);
        return;
    }
    
    const entry = pages[data.component];
    if (!entry) {
        console.error(`No React page registered for "${data.component}" (see src/app.tsx)`);
        return;
    }

    // Auth gate: runs before any React or page chunk is downloaded.
    if (entry.access !== "public") {
        const loggedIn = getValidToken() !== null;
        if (entry.access === "protected" && !loggedIn) {
            window.location.replace(config.PORTAL_LOGIN_URL); // login page
            return;
        }
        // if (entry.access === "guest" && loggedIn) {
        //     window.location.replace(config.PORTAL_DASHBOARD_URL);
        //     return;
        // }
    }

    const [{ createRoot }, { StrictMode }, { ErrorBoundary }, { default: Page }, guard] =
        await Promise.all([
            import("react-dom/client"),
            import("react"),
            import("./shared/errorBoundary"),
            entry.load(),
            // Only protected pages need the live session watcher.
            entry.access === "protected" ? import("./shared/auth/requireAuth") : null,
        ]);

    const page = <Page {...data.props} />;
    const RequireAuth = guard?.RequireAuth;

    createRoot(rootEl).render(
        <StrictMode>
            <ErrorBoundary>
                {RequireAuth ? <RequireAuth>{page}</RequireAuth> : page}
                {data.component != "Bot" && <Footer content={data.props.layoutContent} />}
            </ErrorBoundary>
        </StrictMode>,
    );
}

void boot();
